import { isProductSoldOut } from "@/src/utils/products/stock";

/**
 * Cross-branch product identity for the storefront.
 *
 * A seller with two branches stocks the same product in both, and the catalogue
 * carries one Product document per branch — different `_id`, different `sku`,
 * no shared parent id. There is no cross-outlet identity in the data, so we
 * derive one from the fields a genuine clone shares.
 *
 * The key is deliberately strict. Measured against production: Chak & Go's two
 * copies of "Coconut Crunch Date Bites" agree on name, category and price and
 * merge; Parampara's seven same-name pairs disagree on both category and price
 * (₹750 vs ₹1650 on one) and stay separate, which is correct — they are
 * different products that happen to share a name.
 *
 * It also fails in the safe direction. A branch-only price change un-merges a
 * clone and the product renders twice: visible, and easy for a seller to
 * explain. The failure we must never ship is the opposite one — hiding a
 * product, or printing one branch's price over another branch's stock.
 */

/** Anything the storefront renders per outlet: products (nested `outlet`) and combos (flat). */
export interface OutletScoped {
  outletId?: string;
  outlet?: { outletId?: string } | null;
}

/** The subset of `Product` the grouping rules read. */
export interface GroupableProduct extends OutletScoped {
  _id: string;
  name?: string;
  tenantId?: string;
  category?: { _id?: string | number } | null;
  pricing?: { sellingPrice?: number } | null;
  inventory?: { quantity?: number } | null;
  variantCount?: number;
  variants?: Array<{ stockQuantity?: number }>;
}

/** "Holy  Fudge " and "holy fudge" are the same product. */
export function normalizeName(name?: string): string {
  return (name ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * The product's name with a branch qualifier removed.
 *
 * Sellers tag a branch's copy of a product with a location suffix. Sugandhi
 * (tenant 139) lists the identical perfume as "Blue Eclipse Inspired by Bleu de
 * Chanel" at its primary branch and "Blue Eclipse Inspired by Bleu de Chanel —
 * Bengaluru" at its second, so a key built on the raw name matches none of its
 * eight shared products.
 *
 * Only a SPACED dash starts a qualifier. That distinction is what keeps pack
 * sizes apart: "Cold Brew - 250ml" and "Cold Brew - 500ml" both truncate to
 * "cold brew", but a spaced dash is also how sellers write sizes, so the price
 * band in `dedupeToUnique` is the guard that actually separates them — two pack
 * sizes priced within 10% of each other is not a case that occurs. An unspaced
 * dash ("t-shirt") is part of the name and never truncates.
 */
export function canonicalName(name?: string): string {
  const full = normalizeName(name);
  const cut = full.match(/^(.*?)\s+[—–-]\s+/);
  const head = cut ? cut[1].trim() : full;
  // Never let truncation empty the name — "— Bengaluru" alone stays as-is.
  return head || full;
}

/**
 * The embedded outlet is authoritative — portal-api's `enrichProduct` sets it
 * per product; the flat id is the fallback for payloads that omit it.
 */
export function outletIdOf(item: OutletScoped): string | undefined {
  return item.outlet?.outletId ?? item.outletId;
}

/**
 * Delegates to the storefront's own stock rule rather than re-deriving it.
 * `isProductSoldOut` treats an absent quantity as IN stock on purpose, and
 * keeps a product buyable while any variant has units — re-implementing either
 * of those here would make the grid and the grouping disagree about what is
 * sold out.
 */
export function isInStock(item: GroupableProduct): boolean {
  return !isProductSoldOut({
    stock: item.inventory?.quantity,
    variantCount: item.variantCount,
    variants: item.variants,
  });
}

/**
 * The identity two branches' copies of one product share.
 *
 * Any missing component falls back to the product's own `_id`, so incomplete
 * data always forms a group of one. Merging on absent fields would collapse
 * unrelated products — the one outcome worse than showing a duplicate. Note the
 * explicit empty checks: interpolating `undefined` into the key would silently
 * create a shared `"undefined"` bucket that every uncategorised product joins.
 */
export function groupKey(item: GroupableProduct): string {
  const name = canonicalName(item.name);
  const tenantId = String(item.tenantId ?? "").trim();
  const categoryId = String(item.category?._id ?? "").trim();
  const price = item.pricing?.sellingPrice;

  if (!name || !tenantId || !categoryId || typeof price !== "number" || !Number.isFinite(price)) {
    return `id:${item._id}`;
  }
  return `${tenantId}|${name}|${categoryId}`;
}

/**
 * How far two branches' prices may diverge and still be the same product.
 *
 * Sugandhi prices its second branch ₹289 against the primary's ₹299 — a 3.5%
 * branch difference on one perfume. Parampara's same-name rakhis run ₹750
 * against ₹1650, which is a different product wearing a shared name. 10%
 * separates those two cases with room to spare in both directions.
 */
export const PRICE_TOLERANCE = 1.1;

const priceOf = (item: GroupableProduct): number | null => {
  const p = item.pricing?.sellingPrice;
  return typeof p === "number" && Number.isFinite(p) ? p : null;
};

/**
 * Split one name+category bucket into price bands.
 *
 * Members are sorted by price, then a band extends while the next price stays
 * within `PRICE_TOLERANCE` of the band's cheapest member. Anchoring on the
 * cheapest rather than the previous member stops a long chain of small steps
 * from silently merging ₹100 with ₹1000, and sorting first makes the result
 * independent of the order portal-api merged the outlets in.
 */
function priceBands<T extends GroupableProduct>(members: T[]): T[][] {
  if (members.length < 2) return [members];

  const sorted = [...members].sort((a, b) => {
    const pa = priceOf(a);
    const pb = priceOf(b);
    if (pa !== pb) return (pa ?? 0) - (pb ?? 0);
    return a._id < b._id ? -1 : a._id > b._id ? 1 : 0;
  });

  const bands: T[][] = [];
  let band: T[] = [];
  let anchor: number | null = null;

  for (const item of sorted) {
    const price = priceOf(item);
    if (!band.length || price === null || anchor === null || price > anchor * PRICE_TOLERANCE) {
      if (band.length) bands.push(band);
      band = [item];
      anchor = price;
    } else {
      band.push(item);
    }
  }
  if (band.length) bands.push(band);
  return bands;
}

/**
 * One card per product, across every branch the seller runs.
 *
 * Replaces the previous `filterToOutlet`, which showed only the nearest
 * branch's catalogue and therefore hid products that branch does not stock —
 * a buyer near Chak & Go's second outlet saw one product; the seller sells two.
 *
 * Two rules make this safe to run on every store:
 *
 * 1. A group confined to ONE outlet is returned intact. Same-outlet products
 *    that happen to share name, category and price are different things — pack
 *    sizes, a re-listed SKU — and collapsing them would delete a real product.
 *    Single-outlet stores therefore pass through byte-identical.
 *
 * 2. The winner does NOT depend on the buyer's location. The cart is keyed by
 *    `_id` alone (CartContext.getItemQuantity), and the serving outlet
 *    re-resolves on every address change — so a location-dependent winner would
 *    silently reset the quantity stepper and let one product into the cart
 *    twice from two branches. A stable winner also keeps SSR, the sitemap and
 *    JSON-LD pointing at the same URL for a product.
 *
 * Order follows each group's first appearance, so the caller's sort survives.
 */
export function dedupeToUnique<T extends GroupableProduct>(items: T[]): T[] {
  const groups = new Map<string, T[]>();
  const order: string[] = [];

  for (const item of items) {
    const key = groupKey(item);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      groups.set(key, [item]);
      order.push(key);
    }
  }

  const survivors = new Set<T>();
  for (const key of order) {
    const bucket = groups.get(key)!;
    if (bucket.length === 1) {
      survivors.add(bucket[0]);
      continue;
    }
    // A shared name and category is not yet a shared product — Parampara lists
    // two different rakhis as "Classic Silver Rakhi" at ₹700 and ₹800. Split the
    // bucket into price bands first; only a band is a real group.
    for (const members of priceBands(bucket)) {
      for (const winner of collapseAcrossOutlets(members)) survivors.add(winner);
    }
  }

  // Filter the ORIGINAL array rather than emitting group by group. Grouping and
  // price banding both reorder internally, and rebuilding from them would
  // scramble the caller's sort — which for a store where nothing collapses
  // means shuffling a catalogue for no reason. This way a no-op input returns
  // byte-identical output, and a collapsed one keeps the surviving cards in the
  // order the caller put them in.
  return items.filter((item) => survivors.has(item));
}

/**
 * Reduce one price band to the cards it genuinely represents.
 */
function collapseAcrossOutlets<T extends GroupableProduct>(members: T[]): T[] {
  {
    if (members.length === 1) return members;

    // Split the group by branch, then keep exactly ONE branch's worth of it.
    //
    // The card count is the largest number of members any single outlet holds.
    // That branch is direct evidence of how many genuinely distinct products
    // live under this key: if one outlet lists "Tomato Farm" twice at the same
    // price, those are two real products (two pack sizes, say) and collapsing
    // them would delete one. Emitting fewer loses a product; emitting more
    // shows the duplicate we came here to remove.
    //
    // A group confined to one branch therefore passes through untouched, which
    // is what makes this a no-op for single-outlet stores.
    const byOutlet = new Map<string, T[]>();
    for (const m of members) {
      const oid = outletIdOf(m) ?? "";
      const branch = byOutlet.get(oid);
      if (branch) branch.push(m);
      else byOutlet.set(oid, [m]);
    }

    // Which branch represents the group. Every comparison reads the documents
    // themselves — never array order, never the buyer's location — so the
    // result is stable across requests and identical on server and client.
    let winner: T[] | undefined;
    for (const branch of byOutlet.values()) {
      if (!winner) {
        winner = branch;
        continue;
      }
      if (branch.length !== winner.length) {
        if (branch.length > winner.length) winner = branch;
        continue;
      }
      const branchStock = branch.filter(isInStock).length;
      const winnerStock = winner.filter(isInStock).length;
      if (branchStock !== winnerStock) {
        if (branchStock > winnerStock) winner = branch;
        continue;
      }
      if (branch[0]._id < winner[0]._id) winner = branch;
    }
    return winner ?? members;
  }
}

/**
 * Translate a seller's hand-picked ids into the items actually on screen.
 *
 * Curated lists store Mongo `_id`s, and the copy a seller curated against may
 * not be the copy grouping chose to render. A list that matches nothing leaves
 * `BestsellersRail` rendering `null`, so the section the seller switched on
 * silently disappears — which is why this ships alongside the grouping itself.
 *
 * Three keys are tried in order, and the fallbacks matter as much as the first:
 *
 *   1. exact `_id`     — the pick may already be on screen
 *   2. `groupKey`      — the pick is another branch's copy of a visible product
 *   3. normalized name — what `resolveCuratedToOutlet` used before this existed
 *
 * Keeping (3) is deliberate. `groupKey` is stricter than a name match, so
 * dropping the name fallback would UNRESOLVE picks that work today — two clones
 * whose prices drifted by a rupee, for instance. This resolver may only ever add
 * matches. It is also what lets combos, which have no category or price shape to
 * key on, use the same function.
 */
export function resolveCuratedToVisible<T extends { _id: string; name?: string }>(
  ids: string[] | undefined,
  union: T[],
  visible: T[],
  keyOf: (item: T) => string = (item) => groupKey(item as unknown as GroupableProduct)
): T[] {
  if (!ids?.length) return [];

  const byId = new Map(visible.map((i) => [i._id, i]));
  const byKey = new Map<string, T>();
  const byName = new Map<string, T>();
  for (const item of visible) {
    const key = keyOf(item);
    // First wins, so a duplicate key resolves deterministically.
    if (key && !byKey.has(key)) byKey.set(key, item);
    const name = normalizeName(item.name);
    if (name && !byName.has(name)) byName.set(name, item);
  }
  const unionById = new Map(union.map((i) => [i._id, i]));

  /**
   * `groupKey` buckets by name and category only — price banding happens inside
   * `dedupeToUnique` — so a key match alone could send a ₹750 pick to a ₹1650
   * card. Re-apply the same tolerance here, and treat a missing price on either
   * side as "no objection" so this can never be stricter than the old name match.
   */
  const priceCompatible = (a: T, b: T): boolean => {
    const pa = (a as unknown as GroupableProduct).pricing?.sellingPrice;
    const pb = (b as unknown as GroupableProduct).pricing?.sellingPrice;
    if (typeof pa !== "number" || typeof pb !== "number") return true;
    const [lo, hi] = pa <= pb ? [pa, pb] : [pb, pa];
    return lo > 0 ? hi <= lo * PRICE_TOLERANCE : true;
  };

  const out: T[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    const pinned = unionById.get(id);
    const viaKey = pinned ? byKey.get(keyOf(pinned)) : undefined;
    const viaName = pinned ? byName.get(normalizeName(pinned.name)) : undefined;
    const match =
      byId.get(id) ??
      (viaKey && pinned && priceCompatible(pinned, viaKey) ? viaKey : undefined) ??
      (viaName && pinned && priceCompatible(pinned, viaName) ? viaName : undefined);
    if (match && !seen.has(match._id)) {
      seen.add(match._id);
      out.push(match);
    }
  }
  return out;
}

/**
 * Narrow an outlet-spanning list to one branch, keeping the empty-result valve.
 *
 * This is what combos still need. Unlike products they are authored per outlet,
 * so offering a Bangalore buyer a bundle that ships from Mumbai is not a
 * duplicate — it is wrong. But narrowing to nothing is worse than narrowing
 * imprecisely: `BundleAndSave` renders `null` on an empty list, so a store whose
 * serving branch has no combos of its own would lose the whole section. The
 * valve fires only on a total wipeout; a partial result passes through.
 */
export function narrowToOutlet<T extends OutletScoped>(items: T[], outletId: string): T[] {
  if (!outletId) return items;
  const kept = items.filter((item) => outletIdOf(item) === outletId);
  return kept.length > 0 ? kept : items;
}
